import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import AddOrderItem from '../order-items/add/add-order-item.component';
import AdminModal from '../../reusable/admin-modal/admin-modal.component';
import Button from '../../reusable/button/button.component';
import InputCurrency from '../../reusable/inputs/currency.input';
import Spinner from '../../reusable/spinner/spinner.component';
import UpdateOrderItem from '../order-items/update/update-order-item.component';

import Client from '../../../tools/client';

import {
  ColumnContainer,
  ContentContainer,
  DeleteButton,
  Input,
  MainContainer,
  Option,
  RowContainer,
  Select,
  Subtitle,
  Textarea,
  Title
} from '../../../styles/component.styles';

const client = new Client();

const UpdateOrder = ({ order, updateOrder, cancelOrder, showUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [orderItems, setOrderItems] = useState(order.OrderItems ?? []);
  const [newOrderItems, setNewOrderItems] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  const [status, setStatus] = useState(order.status ?? '');
  const [refId, setRefId] = useState(order.refId ?? '');
  const [paymentType, setPaymentType] = useState(order.paymentType ?? '');
  const [paymentLink, setPaymentLink] = useState(order.paymentLink ?? '');

  const [billingFirstName, setBillingFirstName] = useState(order.billingAddress.firstName ?? '');
  const [billingLastName, setBillingLastName] = useState(order.billingAddress.lastName ?? '');
  const [billingAddressOne, setBillingAddressOne] = useState(order.billingAddress.addressOne ?? '');
  const [billingAddressTwo, setBillingAddressTwo] = useState(order.billingAddress.addressTwo ?? '');
  const [billingCity, setBillingCity] = useState(order.billingAddress.city ?? '');
  const [billingState, setBillingState] = useState(order.billingAddress.state ?? '');
  const [billingZipCode, setBillingZipCode] = useState(order.billingAddress.zipCode ?? '');

  const [shippingFirstName, setShippingFirstName] = useState(order.shippingAddress.firstName ?? '');
  const [shippingLastName, setShippingLastName] = useState(order.shippingAddress.lastName ?? '');
  const [shippingAddressOne, setShippingAddressOne] = useState(order.shippingAddress.addressOne ?? '');
  const [shippingAddressTwo, setShippingAddressTwo] = useState(order.shippingAddress.addressTwo ?? '');
  const [shippingCity, setShippingCity] = useState(order.shippingAddress.city ?? '');
  const [shippingState, setShippingState] = useState(order.shippingAddress.state ?? '');
  const [shippingZipCode, setShippingZipCode] = useState(order.shippingAddress.zipCode ?? '');

  const [deliveryInsurance, setDeliveryInsurance] = useState(order.deliveryInsurance ?? false);
  const [deliveryInsuranceTotal, setDeliveryInsuranceTotal] = useState(order.deliveryInsuranceTotal ?? 0);
  const [shippingTotal, setShippingTotal] = useState(order.shippingTotal ?? 0);
  const [total, setTotal] = useState(order.total ?? 0);
  const [notes, setNotes] = useState(order.notes ?? '');

  useEffect(() => {
    const getInventories = async () => {
      const res = await client.getInventories();
      setInventories(res.rows);
      setLoading(false);
    };
    getInventories();
  }, []);

  const updateOrderItem = (updatedItem) => {
    setOrderItems((prev) =>
      prev.map((item) => (item.id === updatedItem.id ? updatedItem : item))
    );
  };

  const handleNewItemChange = (tempId, updatedItem) => {
    setNewOrderItems((prev) =>
      prev.map((item) => (item.tempId === tempId ? { ...item, ...updatedItem } : item))
    );
  };

  const handleRemoveNewItem = (tempId) => {
    setNewOrderItems((prev) => prev.filter((item) => item.tempId !== tempId));
  };

  const confirmRemoveOrderItem = (item) => {
    setItemToRemove(item);
    setShowRemoveModal(true);
  };

  const removeOrderItem = () => {
    setOrderItems((prev) => prev.filter((item) => item.id !== itemToRemove.id));
    setItemToRemove(null);
    setShowRemoveModal(false);
  };

  const addNewItemRow = () => {
    setNewOrderItems((prev) => [
      ...prev,
      {
        tempId: uuidv4(),
        productId: null,
        inventoryId: null,
        quantity: 1,
        unitPrice: 0
      }
    ]);
  };

  const existingInventoryIds = [
    ...orderItems.map((item) => item.inventoryId),
    ...newOrderItems.map((item) => item.inventoryId)
  ].filter(Boolean);

  const submitUpdate = async () => {
    setLoading(true);
    const payload = {
      id: order.id,
      billingAddress: {
        firstName: billingFirstName,
        lastName: billingLastName,
        addressOne: billingAddressOne,
        addressTwo: billingAddressTwo,
        city: billingCity,
        state: billingState,
        zipCode: billingZipCode
      },
      shippingAddress: {
        firstName: shippingFirstName,
        lastName: shippingLastName,
        addressOne: shippingAddressOne,
        addressTwo: shippingAddressTwo,
        city: shippingCity,
        state: shippingState,
        zipCode: shippingZipCode
      },
      status,
      refId,
      paymentLink,
      paymentType,
      orderItems: [
        ...orderItems.map((oi) => ({
          id: oi.id,
          productId: oi.productId,
          inventoryId: oi.inventoryId,
          quantity: oi.quantity,
          unitPrice: oi.unitPrice,
          totalPrice: oi.quantity * oi.unitPrice
        })),
        ...newOrderItems.map(({ tempId, ...oi }) => ({
          ...oi,
          totalPrice: oi.quantity * oi.unitPrice
        }))
      ],
      deliveryInsurance,
      deliveryInsuranceTotal,
      shippingTotal,
      total,
      notes
    };

    await updateOrder(payload);
    setLoading(false);
  };

  return (
    <MainContainer>
      <AdminModal
        show={showRemoveModal}
        setShow={setShowRemoveModal}
        title={'Remove Order Item'}
        message={`Are you sure you want to remove this item? Inventory will be restocked when the order is saved.`}
        cancelText={'Cancel'}
        action={removeOrderItem}
        actionText={'Remove Item'}
      />

      {loading ? (
        <Spinner />
      ) : (
        <ContentContainer>
          <Title>Update Order</Title>

          <ColumnContainer>
            <Input value={refId} onChange={(e) => setRefId(e.target.value)} placeholder={'Reference ID'} />
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <Option value={'new'}>New</Option>
              <Option value={'billed'}>Billed</Option>
              <Option value={'paid'}>Paid</Option>
              <Option value={'processing'}>Processing</Option>
              <Option value={'shipped'}>Shipped</Option>
            </Select>
            <Select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              <Option value={'card'}>Card</Option>
              <Option value={'bitcoin'}>Bitcoin</Option>
              <Option value={'ethereum'}>Ethereum</Option>
            </Select>
            <Input value={paymentLink} onChange={(e) => setPaymentLink(e.target.value)} placeholder={'Payment Link'} />
          </ColumnContainer>

          <ColumnContainer>
            <Subtitle>Billing Address</Subtitle>
            <Input value={billingFirstName} onChange={(e) => setBillingFirstName(e.target.value)} placeholder={'First Name'} />
            <Input value={billingLastName} onChange={(e) => setBillingLastName(e.target.value)} placeholder={''} />
            <Input value={billingAddressOne} onChange={(e) => setBillingAddressOne(e.target.value)} placeholder={''} />
            <Input value={billingAddressTwo} onChange={(e) => setBillingAddressTwo(e.target.value)} placeholder={''} />
            <Input value={billingCity} onChange={(e) => setBillingCity(e.target.value)} placeholder={''} />
            <Input value={billingState} onChange={(e) => setBillingState(e.target.value)} placeholder={''} />
            <Input value={billingZipCode} onChange={(e) => setBillingZipCode(e.target.value)} placeholder={''} />
          </ColumnContainer>

          <ColumnContainer>
            <Subtitle>Shipping Address</Subtitle>
            <Input value={shippingFirstName} onChange={(e) => setShippingFirstName(e.target.value)} placeholder={'First Name'} />
            <Input value={shippingLastName} onChange={(e) => setShippingLastName(e.target.value)} placeholder={''} />
            <Input value={shippingAddressOne} onChange={(e) => setShippingAddressOne(e.target.value)} placeholder={''} />
            <Input value={shippingAddressTwo} onChange={(e) => setShippingAddressTwo(e.target.value)} placeholder={''} />
            <Input value={shippingCity} onChange={(e) => setShippingCity(e.target.value)} placeholder={''} />
            <Input value={shippingState} onChange={(e) => setShippingState(e.target.value)} placeholder={''} />
            <Input value={shippingZipCode} onChange={(e) => setShippingZipCode(e.target.value)} placeholder={''} />
          </ColumnContainer>

          {orderItems.map((item) => {
            const filteredInventories = inventories.filter(
              (inv) => inv.id === item.inventoryId || !existingInventoryIds.includes(inv.id)
            );

            return (
              <UpdateOrderItem
                key={item.id}
                orderItem={item}
                inventories={filteredInventories}
                updateOrderItem={updateOrderItem}
                onRemove={() => confirmRemoveOrderItem(item)}
              />
            );
          })}

          {newOrderItems.map((item) => {
            const filteredInventories = inventories.filter(
              (inv) => inv.id === item.inventoryId || !existingInventoryIds.includes(inv.id)
            );

            return (
              <AddOrderItem
                key={item.tempId}
                inventories={filteredInventories}
                onChange={(updatedItem) => handleNewItemChange(item.tempId, updatedItem)}
                onRemove={() => handleRemoveNewItem(item.tempId)}
              />
            );
          })}

          {inventories.length > existingInventoryIds.length && (
            <ColumnContainer margin={'20px 0'}>
              <Button onClick={addNewItemRow}>Add Item</Button>
            </ColumnContainer>
          )}

          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={'Notes'} />
          <InputCurrency value={total} onChange={setTotal} placeholder={'Total'} />

          {order.status !== 'canceled' && order.status !== 'shipped' && (
            <DeleteButton onClick={cancelOrder}>Cancel Order</DeleteButton>
          )}

          <RowContainer margin={'20px 0'}>
            <Button onClick={() => showUpdate(false)}>Cancel</Button>
            {order.status !== 'canceled' && (
              <Button onClick={submitUpdate}>Update Order</Button>
            )}
          </RowContainer>
        </ContentContainer>
      )}
    </MainContainer>
  );
};

export default UpdateOrder;